import { tzOpts } from "@/lib/tz";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ScrollText, Download, RefreshCw, MessageSquare, Users, KeyRound, Database,
  Signal, Cpu, ChevronDown, ChevronUp, Building2, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight,
} from "lucide-react";

const TABS = [
  { key: "all", label: "Todas", icon: ScrollText },
  { key: "messaging", label: "Mensagens", icon: MessageSquare },
  { key: "client", label: "Clientes", icon: Users },
  { key: "auth", label: "Login", icon: KeyRound },
  { key: "database", label: "Banco de Dados", icon: Database },
  { key: "evolution", label: "Evolution API", icon: Signal },
  { key: "system", label: "Sistema", icon: Cpu },
];

const LEVEL_STYLES = {
  error: "bg-red-500/15 text-red-300 border border-red-500/30",
  warning: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
};

export default function Logs() {
  const { user } = useAuth();
  const isSuper = !!user?.is_superadmin;

  const [tab, setTab] = useState("all");
  const [days, setDays] = useState("30");
  const [level, setLevel] = useState("all");
  const [search, setSearch] = useState("");
  const [tenantId, setTenantId] = useState("all");
  const [tenants, setTenants] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [retentionDays, setRetentionDays] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 25;

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const params = {
        days, category: tab === "all" ? "" : tab, level: level === "all" ? "" : level, search,
        page: p, limit: PAGE_SIZE,
      };
      if (isSuper && tenantId !== "all") params.tenant_id = tenantId;
      const { data } = await api.get("/logs", { params });
      setRows(data.items || []);
      setPage(data.page || 1);
      setPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, days, level, tenantId]);

  useEffect(() => {
    if (isSuper) {
      api.get("/admin/tenants").then(({ data }) => setTenants(data)).catch(() => {});
      api.get("/admin/logs/retention").then(({ data }) => setRetentionDays(data.days)).catch(() => {});
    }
  }, [isSuper]);

  const search_ = () => load(1);

  const exportCsv = async () => {
    try {
      const params = { days, category: tab === "all" ? "" : tab, level: level === "all" ? "" : level, search };
      if (isSuper && tenantId !== "all") params.tenant_id = tenantId;
      const res = await api.get("/logs/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `logs_cobrazap_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const saveRetention = async () => {
    try {
      await api.put("/admin/logs/retention", { days: retentionDays });
      toast.success("Retenção de logs atualizada");
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Diagnóstico</div>
          <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">Logs</h1>
          <p className="text-sm text-slate-500 mt-2">
            Só registra erros e avisos — nunca operações normais. Os registros expiram sozinhos depois de{" "}
            {retentionDays ?? 90} dias, então isso nunca enche o banco.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="logs-refresh-btn" onClick={() => load(page)} variant="outline" className="border-slate-700 hover:bg-slate-800 hover:text-white">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
          <Button data-testid="logs-export-btn" onClick={exportCsv} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
            <Download className="w-4 h-4 mr-1.5" /> Exportar CSV
          </Button>
        </div>
      </header>

      {/* -------- Abas -------- */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`logs-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors ${
                tab === t.key
                  ? "border-emerald-500 bg-emerald-500/15 text-emerald-300"
                  : "border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-300"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* -------- Filtros -------- */}
      <Card className="bg-slate-900/50 border-slate-800 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Período</Label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="mt-1 bg-slate-950 border-slate-800 text-white h-9 w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                <SelectItem value="1">Últimas 24h</SelectItem>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="90">Últimos 90 dias</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Nível</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="mt-1 bg-slate-950 border-slate-800 text-white h-9 w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="error">Erro</SelectItem>
                <SelectItem value="warning">Aviso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isSuper && (
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Empresa</Label>
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger data-testid="logs-tenant-select" className="mt-1 bg-slate-950 border-slate-800 text-white h-9 w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                  <SelectItem value="all">
                    <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Todas as empresas</span>
                  </SelectItem>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex-1 min-w-[200px]">
            <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Buscar na mensagem</Label>
            <Input
              data-testid="logs-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search_()}
              placeholder="Ex.: timeout, conexão recusada, 401..."
              className="mt-1 bg-slate-950 border-slate-800 text-white"
            />
          </div>
          <Button onClick={search_} variant="outline" className="border-slate-700 hover:bg-slate-800 hover:text-white">
            Filtrar
          </Button>
        </div>

        {isSuper && (
          <div className="flex items-center gap-3 pt-4 mt-4 border-t border-slate-800/60">
            <Label className="text-slate-500 text-xs">Guardar logs por</Label>
            <Input
              type="number" min={7} max={365}
              value={retentionDays ?? 90}
              onChange={(e) => setRetentionDays(parseInt(e.target.value, 10) || 90)}
              className="bg-slate-950 border-slate-800 text-white w-20 h-8 text-xs"
            />
            <span className="text-xs text-slate-500">dias antes de expirar sozinho</span>
            <Button size="sm" onClick={saveRetention} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold h-8 text-xs">
              Salvar
            </Button>
          </div>
        )}
      </Card>

      {/* -------- Tabela -------- */}
      <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
            <tr>
              <th className="text-left py-3 px-4 w-8"></th>
              <th className="text-left py-3 px-4">Data/Hora</th>
              <th className="text-left py-3 px-4">Nível</th>
              {tab === "all" && <th className="text-left py-3 px-4">Categoria</th>}
              {isSuper && tenantId === "all" && <th className="text-left py-3 px-4">Empresa</th>}
              <th className="text-left py-3 px-4">Mensagem</th>
            </tr>
          </thead>
          <tbody data-testid="logs-tbody">
            {rows.map((r) => (
              <>
                <tr
                  key={r._id}
                  onClick={() => setExpanded(expanded === r._id ? null : r._id)}
                  className="border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer"
                >
                  <td className="py-2.5 px-4 text-slate-500">
                    {r.details && (expanded === r._id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                  </td>
                  <td className="py-2.5 px-4 font-mono-jb text-xs text-slate-400 whitespace-nowrap">
                    {new Date(r.created_at + "Z").toLocaleString("pt-BR", tzOpts())}
                  </td>
                  <td className="py-2.5 px-4">
                    <Badge className={LEVEL_STYLES[r.level] || LEVEL_STYLES.error}>{r.level === "error" ? "Erro" : "Aviso"}</Badge>
                  </td>
                  {tab === "all" && (
                    <td className="py-2.5 px-4">
                      <Badge className="bg-slate-500/15 text-slate-300 border border-slate-500/30">{r.category_label}</Badge>
                    </td>
                  )}
                  {isSuper && tenantId === "all" && (
                    <td className="py-2.5 px-4 text-slate-400 text-xs">{r.tenant_name || "—"}</td>
                  )}
                  <td className="py-2.5 px-4 text-slate-300">{r.message}</td>
                </tr>
                {expanded === r._id && r.details && (
                  <tr key={`${r._id}-details`} className="border-b border-slate-800/60 bg-slate-950/50">
                    <td></td>
                    <td colSpan={5} className="py-3 px-4">
                      <pre className="text-[11px] text-slate-500 font-mono-jb whitespace-pre-wrap break-all">
                        {JSON.stringify(r.details, null, 2)}
                      </pre>
                      {r.source && <div className="text-[11px] text-slate-600 mt-1">Origem: {r.source}</div>}
                    </td>
                  </tr>
                )}
              </>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="text-center py-10 text-slate-500">
                  Nenhum erro registrado nesse período. 🎉
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800 flex-wrap gap-2">
            <div className="text-xs text-slate-500">
              Página {page} de {pages} · {total} registro(s) no total
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="logs-page-first"
                onClick={() => load(1)}
                disabled={page === 1}
                className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                data-testid="logs-page-prev"
                onClick={() => load(Math.max(1, page - 1))}
                disabled={page === 1}
                className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-slate-400 px-2 font-mono-jb">{page} / {pages}</span>
              <button
                data-testid="logs-page-next"
                onClick={() => load(Math.min(pages, page + 1))}
                disabled={page === pages}
                className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                data-testid="logs-page-last"
                onClick={() => load(pages)}
                disabled={page === pages}
                className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
